import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';

import { Stage, TeaserContainer, JobTeaser, Container } from '@ccg/fischerappelt';

import relatedJobsQuery from '../../../graphql/queries/relatedJobs.graphql';

import Embeds from '../../shared/Embeds/Embeds';

import DynamicTeaser from '../../shared/TeaserContainer/subcomponents/DynamicTeaser';

import PageTemplate from '../templates/page';
import { removeSpecialCharacters } from '../../../helper';

import { withTranslation } from '../../../i18n';

const JobTemplate = ({ data, site, t }) => {
  if (!data.entry) return null;

  const { cookieLayer, entry, footer, teaserSettings, seoDefaults, navigation, social } = data;
  const { relatedJobsCount } = teaserSettings;
  const { jobsOverviewPage } = navigation;

  const {
    contentType,
    title,
    subline,
    embeds,
    embedsCopy,
    locations,
    companies,
    id,
    postDate,
    employmentType,
    seniority,
    schedule
  } = entry;

  const limit = relatedJobsCount || 4;

  const { data: relatedData } = useQuery(relatedJobsQuery, {
    variables: {
      site,
      currentId: id,
      limit,
      relatedTo: ['and', ...[...locations, ...companies].map(item => parseFloat(item.id))]
    }
  });

  const relatedEntries = relatedData && relatedData.relatedEntries;

  return (
    <PageTemplate
      seo={entry}
      seoDefaults={seoDefaults}
      navigation={navigation}
      footer={footer}
      social={social}
      cookieLayer={cookieLayer}
      hasAccentLine
    >
      <Stage
        size={contentType === 'advertising' ? 'large' : 'small'}
        headline={title}
        title={removeSpecialCharacters(title)}
        subline={subline}
        tags={locations}
        newsFormat={companies[0] && companies[0].title}
        employmentType={employmentType}
        seniority={seniority}
        schedule={schedule}
        postDate={new Date(postDate).toLocaleDateString(undefined, {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })}
        breadcrumbLink={
          jobsOverviewPage && jobsOverviewPage[0]
            ? {
                label: removeSpecialCharacters(jobsOverviewPage[0].title),
                href: {
                  pathname: '/standard',
                  query: { uri: jobsOverviewPage[0].uri }
                },
                asPath: `/${jobsOverviewPage[0].uri}`
              }
            : undefined
        }
      />

      <Embeds embedsCopy={embedsCopy} embeds={embeds} />

      {companies && companies.length > 0 && (
        <TeaserContainer>
          {entry.companies.map((location, index) => (
            <DynamicTeaser index={index + 1} key={location.id} {...location} />
          ))}
        </TeaserContainer>
      )}

      {locations && locations.length > 0 && (
        <TeaserContainer>
          {entry.locations.map((location, index) => (
            <DynamicTeaser index={index + 1} key={location.id} {...location} />
          ))}
        </TeaserContainer>
      )}

      {relatedEntries && relatedEntries.length > 0 && (
        <Container variant="default" headline={t('teaser.relatedJobs')}>
          <TeaserContainer>
            {relatedEntries &&
              relatedEntries.map(item => (
                <JobTeaser
                  key={item.id}
                  title={item.title}
                  locations={item.locations}
                  companies={item.companies}
                  link={{
                    href: { pathname: '/job', query: { slug: item.slug } },
                    asPath: `/${item.uri}`
                  }}
                />
              ))}
          </TeaserContainer>
        </Container>
      )}
    </PageTemplate>
  );
};

JobTemplate.propTypes = {
  site: PropTypes.string.isRequired,
  data: PropTypes.shape().isRequired,
  t: PropTypes.func.isRequired
};

JobTemplate.defaultProps = {};

export default withTranslation('common')(JobTemplate);
